import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, Quote, ChevronDown } from 'lucide-react';
import VitrineLayout from '../../components/layouts/VitrineLayout';

// ============================================================================
// IMAGERY — Hero AI-generated (Nano Banana). Local /realisations/ premium images.
// ============================================================================
const HERO_MARBLE = "/hero-granipal.jpg";

const STONE_DETAIL  = "https://plus.unsplash.com/premium_photo-1764687881329-b8787a4fc03c?fm=jpg&q=85&w=1400&auto=format&fit=crop";

// Coherent material-driven realisation imagery — local AI-generated images
const ACT_KITCHEN  = "/realisations/plans-0.jpg";       // Calacatta Veneziano
const ACT_BATHROOM = "/realisations/bains-1.jpg";       // Marbre Carrare Statuario
const ACT_STAIRS   = "/realisations/escaliers-0.jpg";   // Marbre Bianco Carrara
const ACT_TABLE    = "/realisations/tables-0.jpg";      // Onyx Honey
const ACT_CLADDING = "/realisations/revetements-1.jpg"; // Xtone Bronzo Imperiale
const ACT_VASQUE   = "/realisations/vasques-2.jpg";     // Dekton Kelya

// SVG paper grain (very subtle)
const GRAIN_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220">' +
    '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>' +
    '<feColorMatrix type="saturate" values="0"/></filter>' +
    '<rect width="100%" height="100%" filter="url(#n)" opacity="0.55"/></svg>'
);
const GRAIN_BG = `url("data:image/svg+xml,${GRAIN_SVG}")`;

// Bronze patiné — grain plus contrasté pour effet "pierre forgée" à l'intérieur du titre GRANIPAL
const STONE_GRAIN_SVG = encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="340" height="340">' +
    '<filter id="s">' +
      '<feTurbulence type="fractalNoise" baseFrequency="0.55" numOctaves="3" seed="7" stitchTiles="stitch"/>' +
      '<feColorMatrix type="matrix" values="0 0 0 0 0.16  0 0 0 0 0.09  0 0 0 0 0.05  0 0 0 1 0"/>' +
    '</filter>' +
    '<rect width="100%" height="100%" filter="url(#s)"/>' +
  '</svg>'
);
const STONE_GRAIN_BG = `url("data:image/svg+xml,${STONE_GRAIN_SVG}")`;

// === Palette luxe : cream → bronze → cocoa ===
const COPPER       = '#B8895A';
const COPPER_LIGHT = '#D4A878';
const COPPER_DARK  = '#8B5E2F';
const BRONZE       = '#A47148';
const LOGO_RED     = '#8D0705';   // Rouge du logo Granipal (R:141 V:7 B:5)
const INK          = '#2B1C12';
const INK_SOFT     = '#6B5340';
const CREAM        = '#F5E6D0';
const BEIGE        = '#D9B583';
const DARK_BG      = '#120B07';   // cocoa profond
const DARK_BG_2    = '#1A130D';

// Display font for GRANIPAL/H2 — DM Serif Display (high-contrast Didone modern luxe)
const DISPLAY_FF = "'DM Serif Display', 'Fraunces', 'Cormorant Garamond', serif";

// Gradient cuivré pour boutons / accents
const COPPER_GRAD = `linear-gradient(135deg, ${COPPER_LIGHT} 0%, ${COPPER} 45%, ${COPPER_DARK} 100%)`;

// === SVG hand-drawn architect sketches (process) ===
const sketchProps = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.1, strokeLinecap: 'round', strokeLinejoin: 'round' };
const IconDemande = (p) => (<svg viewBox="0 0 64 64" {...p} {...sketchProps}><path d="M14 10h26l10 10v34H14z" /><path d="M40 10v10h10" /><path d="M21 30h22M21 38h22M21 46h14" /></svg>);
const IconDevis = (p) => (<svg viewBox="0 0 64 64" {...p} {...sketchProps}><rect x="12" y="10" width="40" height="44" rx="1.5" /><path d="M20 20h24" /><path d="M20 28h6M30 28h14M20 36h6M30 36h14M20 44h6M30 44h10" /><path d="M44 26l6 6-6 6" opacity="0.45" /></svg>);
const IconValidation = (p) => (<svg viewBox="0 0 64 64" {...p} {...sketchProps}><path d="M8 32l10 12 22-26" /><circle cx="44" cy="40" r="14" /><path d="M38 40l4 4 10-10" opacity="0.5" /></svg>);
const IconFabrication = (p) => (<svg viewBox="0 0 64 64" {...p} {...sketchProps}><path d="M32 8v8" /><path d="M32 16l-14 38" /><path d="M32 16l14 38" /><circle cx="32" cy="16" r="3.2" /><path d="M22 42h20" /></svg>);
const IconLivraison = (p) => (<svg viewBox="0 0 64 64" {...p} {...sketchProps}><path d="M6 22h28v22H6z" /><path d="M34 28h14l8 8v8H34z" /><circle cx="18" cy="48" r="4" /><circle cx="46" cy="48" r="4" /></svg>);

const PROCESS = [
  { n: '01', title: 'Demande',          text: 'Vous nous transmettez vos plans, votre matière et vos envies.',                                Icon: IconDemande },
  { n: '02', title: 'Devis',            text: 'Sous quelques jours, un devis détaillé et transparent.',                                       Icon: IconDevis },
  { n: '03', title: 'Validation',       text: "À l'acompte, votre projet entre en atelier.",                                                   Icon: IconValidation },
  { n: '04', title: 'Fabrication',      text: 'Façonnage manuel et précision millimétrique par nos marbriers, dans notre atelier.',           Icon: IconFabrication },
  { n: '05', title: 'Livraison & Pose', text: 'Nos marbriers livrent et posent chez vous, finitions réalisées à la main.',                    Icon: IconLivraison },
];

// Per-category photos (3 each)
const REAL_PHOTOS = {
  'Plans de travail': ['/realisations/plans-0.jpg', '/realisations/plans-1.jpg', '/realisations/plans-2.jpg'],
  'Salles de bain':   ['/realisations/bains-0.jpg', '/realisations/bains-1.jpg', '/realisations/bains-2.jpg'],
  'Tables':           ['/realisations/tables-0.jpg', '/realisations/tables-1.jpg', '/realisations/tables-2.jpg'],
  'Escaliers':        ['/realisations/escaliers-0.jpg', '/realisations/escaliers-1.jpg', '/realisations/escaliers-2.jpg'],
  'Revêtements':      ['/realisations/revetements-0.jpg', '/realisations/revetements-1.jpg', '/realisations/revetements-2.jpg'],
  'Vasques':          ['/realisations/vasques-0.jpg', '/realisations/vasques-1.jpg', '/realisations/vasques-2.jpg'],
};

const ACTIVITIES = [
  { title: 'Plans de travail', subtitle: 'Cuisine sur-mesure',   material: 'Calacatta Veneziano',     img: ACT_KITCHEN },
  { title: 'Salles de bain',   subtitle: 'Vasques & plans',      material: 'Marbre Carrare Statuario', img: ACT_BATHROOM },
  { title: 'Tables',           subtitle: 'Repas & basses',       material: 'Onyx Honey',               img: ACT_TABLE },
  { title: 'Escaliers',        subtitle: 'Marches & paliers',    material: 'Marbre Bianco Carrara',    img: ACT_STAIRS },
  { title: 'Revêtements',      subtitle: 'Sols & murs',          material: 'Xtone Bronzo Imperiale',   img: ACT_CLADDING },
  { title: 'Vasques',          subtitle: 'Pièces taillées',      material: 'Dekton Kelya',             img: ACT_VASQUE },
];

const BRANDS = ['Dekton', 'Laminam', 'Neolith', 'Silestone', 'Compac', 'Techlam', 'Uniceramica', 'Xtone'];

// Partner brand strip — auto-scroll marquee with logo images.
// Logos pre-processed to be uniform cream silhouettes on transparent BG (see /partner-logos/).
const PARTNER_BRANDS = [
  { name: 'Dekton',      logo: '/partner-logos/dekton.png' },
  { name: 'Laminam',     logo: '/partner-logos/laminam.png' },
  { name: 'Neolith',     logo: '/partner-logos/neolith.png' },
  { name: 'Xtone',       logo: '/partner-logos/xtone.png' },
  { name: 'Compac',      logo: '/partner-logos/compac.png' },
  { name: 'Techlam',     logo: '/partner-logos/techlam.png' },
  { name: 'Silestone',   logo: '/partner-logos/silestone.png' },
  { name: 'Brachot',     logo: '/partner-logos/brachot.png' },
  { name: 'Cosentino',   logo: '/partner-logos/cosentino.png' },
  { name: 'Spadaccini',  logo: '/partner-logos/spadaccini.png' },
  { name: 'Granitrans',  logo: '/partner-logos/granitrans.png' },
  { name: 'Uniceramica', logo: '/partner-logos/uniceramica.png' },
  { name: 'Sensa',       logo: '/partner-logos/sensa.png' },
];

// ============================================================================
// COMPONENTS
// ============================================================================

// Smooth gradient bridge between sections — pure 2-stop fade (no copper band)
function SectionBridge({ from, to, height = 'h-60 md:h-80' }) {
  return (
    <div aria-hidden className={`relative w-full ${height} -mt-px overflow-hidden pointer-events-none`}>
      {/* Pure smooth 2-stop gradient — no harsh band */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(to bottom, ${from} 0%, ${to} 100%)` }}
      />
      {/* Soft copper halo at mid for warmth (low opacity, not a band) */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[140%] blur-3xl opacity-25"
        style={{ background: `radial-gradient(ellipse, rgba(184,137,90,0.35), transparent 65%)` }}
      />
    </div>
  );
}

// Glass card wrapper
function GlassCard({ children, className = '', ...rest }) {
  return (
    <div
      className={`relative backdrop-blur-xl ${className}`}
      style={{
        background: 'linear-gradient(160deg, rgba(245,230,208,0.04) 0%, rgba(184,137,90,0.025) 60%, rgba(20,11,7,0.18) 100%)',
        border: '1px solid rgba(245,230,208,0.10)',
        boxShadow: 'inset 0 1px 0 rgba(245,230,208,0.07), 0 30px 60px -30px rgba(0,0,0,0.6)',
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// Copper halo background spot
function CopperHalo({ x = '50%', y = '50%', size = 700, opacity = 0.10 }) {
  return (
    <div
      aria-hidden
      className="absolute pointer-events-none"
      style={{
        left: x, top: y, width: size, height: size, transform: 'translate(-50%, -50%)',
        background: `radial-gradient(circle, rgba(184,137,90,${opacity}) 0%, rgba(184,137,90,0) 65%)`,
        filter: 'blur(20px)',
      }}
    />
  );
}

// Brand marquee — auto-scrolling horizontal strip.
// Each brand attempts to load its logo image; if missing, falls back to a styled wordmark.
function BrandMarquee() {
  const items = [...PARTNER_BRANDS, ...PARTNER_BRANDS];
  return (
    <div className="relative overflow-hidden" data-testid="brand-marquee" style={{
      maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
      WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
    }}>
      <div
        className="flex items-center gap-12 lg:gap-16 whitespace-nowrap"
        style={{
          animation: 'granipal-marquee 38s linear infinite',
          width: 'max-content',
        }}
      >
        {items.map((b, i) => (
          <BrandLogo key={`${b.name}-${i}`} brand={b} />
        ))}
      </div>
      <style>{`
        @keyframes granipal-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// Brand logo with image-load fallback to text wordmark
function BrandLogo({ brand }) {
  const [errored, setErrored] = useState(false);
  if (errored || !brand.logo) {
    return (
      <span
        className="shrink-0 transition-all duration-500"
        style={{
          fontFamily: DISPLAY_FF,
          fontSize: 'clamp(1.4rem, 2.2vw, 1.95rem)',
          fontWeight: 400,
          color: 'rgba(245,230,208,0.45)',
          letterSpacing: '0.04em',
        }}
      >
        {brand.name}
      </span>
    );
  }
  return (
    <img
      src={brand.logo}
      alt={brand.name}
      onError={() => setErrored(true)}
      className="shrink-0 h-9 lg:h-11 w-auto object-contain transition-all duration-500 hover:opacity-100"
      style={{
        opacity: 0.78,
        // Logos are pre-processed to cream-on-transparent; no extra filter needed.
        maxWidth: 170,
      }}
    />
  );
}

// Realisations — Categories as clickable subtitles, on category change the slider shows
// the photos of that category with left/right arrows for cinematic navigation.
// IMPORTANT: only the photo animates between slides — title/category/text stay fixed.
function RealisationsCategorySlider() {
  const [catIdx, setCatIdx] = useState(0);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const cat = ACTIVITIES[catIdx];
  const photos = REAL_PHOTOS[cat.title] || [cat.img];
  const totalPhotos = photos.length;

  // Auto-rotate photos within selected category (3s)
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setPhotoIdx((i) => (i + 1) % totalPhotos), 3000);
    return () => clearInterval(t);
  }, [paused, totalPhotos, catIdx]);

  // Reset photo idx when category changes
  useEffect(() => { setPhotoIdx(0); }, [catIdx]);

  const goPrev = () => setPhotoIdx((i) => (i - 1 + totalPhotos) % totalPhotos);
  const goNext = () => setPhotoIdx((i) => (i + 1) % totalPhotos);

  return (
    <div className="relative" data-testid="realisations-category-slider">
      {/* Category subtitles — clickable, ALWAYS fixed (no exit animation) */}
      <div className="flex flex-wrap justify-center items-center gap-x-3 gap-y-3 mb-12 lg:mb-14" data-testid="realisations-subcategories">
        {ACTIVITIES.map((a, i) => {
          const active = i === catIdx;
          return (
            <button
              key={a.title}
              onClick={() => setCatIdx(i)}
              className="group relative px-4 py-2 text-[12px] uppercase tracking-[0.18em] font-light transition-all duration-500"
              style={{
                color: active ? '#FFF8EC' : 'rgba(245,230,208,0.55)',
                background: active
                  ? 'linear-gradient(135deg, rgba(212,168,120,0.92) 0%, rgba(184,137,90,0.95) 100%)'
                  : 'transparent',
                border: `1px solid ${active ? 'rgba(245,230,208,0.45)' : 'rgba(212,168,120,0.18)'}`,
                borderRadius: '2px',
                boxShadow: active ? 'inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 22px -8px rgba(184,137,90,0.55)' : 'none',
              }}
              data-testid={`realisations-cat-${i}`}
            >
              {a.title}
            </button>
          );
        })}
      </div>

      {/* Photo slider — ONLY the image crossfades */}
      <div
        className="relative w-full aspect-[16/9] lg:aspect-[21/9] overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence mode="sync">
          <motion.img
            key={cat.title + '-' + photoIdx}
            src={photos[photoIdx]}
            alt={`${cat.title} ${photoIdx + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Cinematic gradient + grain (static) */}
        <div aria-hidden className="absolute inset-0" style={{ background: 'linear-gradient(170deg, rgba(18,11,7,0.10) 30%, rgba(18,11,7,0.55) 75%, rgba(18,11,7,0.90) 100%)' }} />
        <div aria-hidden className="absolute inset-0 ring-1 ring-inset pointer-events-none" style={{ borderColor: 'rgba(245,230,208,0.06)' }} />

        {/* Caption — FIXED, no animation (only text content updates when category changes) */}
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 lg:px-16 pb-9 md:pb-12">
          <div className="flex flex-col gap-2 max-w-2xl">
            <span className="text-[10px] uppercase tracking-[0.42em]" style={{ color: COPPER_LIGHT }}>
              {String(photoIdx + 1).padStart(2, '0')} &nbsp;/&nbsp; {String(totalPhotos).padStart(2, '0')} &nbsp; — &nbsp; {cat.subtitle}
            </span>
            <h3 style={{ fontFamily: DISPLAY_FF, fontWeight: 400, fontSize: 'clamp(1.85rem, 4.5vw, 3.6rem)', color: CREAM, letterSpacing: '0.01em', lineHeight: 1 }}>
              {cat.title}
            </h3>
          </div>
        </div>

        {/* Left/Right arrow controls */}
        <button
          onClick={goPrev}
          aria-label="Photo précédente"
          className="absolute left-4 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center backdrop-blur-md transition-all duration-500 hover:scale-110 z-10"
          style={{ background: 'rgba(20,11,7,0.55)', border: '1px solid rgba(245,230,208,0.30)', color: CREAM, borderRadius: '2px' }}
          data-testid="realisations-prev-photo"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          onClick={goNext}
          aria-label="Photo suivante"
          className="absolute right-4 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center backdrop-blur-md transition-all duration-500 hover:scale-110 z-10"
          style={{ background: 'rgba(184,137,90,0.30)', border: '1px solid rgba(212,168,120,0.55)', color: CREAM, borderRadius: '2px' }}
          data-testid="realisations-next-photo"
        >
          <ArrowRight className="w-5 h-5" />
        </button>

        {/* Photo dots */}
        <div className="absolute bottom-6 right-6 lg:bottom-8 lg:right-8 flex gap-2">
          {photos.map((p, i) => (
            <button
              key={p}
              onClick={() => setPhotoIdx(i)}
              aria-label={`Voir photo ${i + 1}`}
              className="transition-all duration-500"
              style={{
                width: i === photoIdx ? '28px' : '8px',
                height: '4px',
                background: i === photoIdx ? COPPER_LIGHT : 'rgba(245,230,208,0.35)',
                borderRadius: '2px',
              }}
              data-testid={`realisations-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN PAGE
// ============================================================================
export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const marbleY = useTransform(heroProgress, [0, 1], ['0%', '14%']);
  const heroTextY = useTransform(heroProgress, [0, 1], ['0px', '-50px']);
  const heroOpacity = useTransform(heroProgress, [0, 0.85], [1, 0]);

  // Wait for intro animation to finish before triggering hero entrance
  // (creates seamless intro → hero handover)
  const [introReady, setIntroReady] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.sessionStorage.getItem('granipal_intro_played') === '1';
  });

  useEffect(() => {
    const handler = () => setIntroReady(true);
    window.addEventListener('granipal-intro-done', handler);
    return () => window.removeEventListener('granipal-intro-done', handler);
  }, []);

  return (
    <VitrineLayout heroLightMode>

      {/* ============================================================
          HERO — quartzite Taj Mahal, composition centrée
         ============================================================ */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ background: CREAM }}
        data-testid="home-hero"
      >
        <motion.div
          aria-hidden
          style={{ y: marbleY, backgroundImage: `url(${HERO_MARBLE})` }}
          className="absolute inset-0 bg-cover bg-center scale-[1.06]"
        />
        {/* Warm wash + bottom fade to bronze */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at 50% 45%, rgba(255,238,210,0.05) 0%, rgba(255,232,200,0) 55%), linear-gradient(180deg, rgba(245,230,208,0.18) 0%, rgba(245,230,208,0) 22%, rgba(40,28,18,0.30) 100%)',
          }}
        />
        {/* Grain */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.18]"
          style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }}
        />

        {/* Bottom dark fade — bakes the transition INTO hero, no visible bright band */}
        <div
          aria-hidden
          className="absolute bottom-0 left-0 right-0 h-[40vh] pointer-events-none"
          style={{
            background: `linear-gradient(to bottom, transparent 0%, rgba(18,11,7,0.15) 35%, rgba(18,11,7,0.75) 80%, ${DARK_BG} 100%)`,
          }}
        />

        {/* CENTERED HERO BLOCK */}
        <motion.div
          style={{ y: heroTextY, opacity: heroOpacity }}
          className="relative z-10 px-6 lg:px-12 w-full max-w-5xl mx-auto text-center"
        >
          {/* GRANIPAL — texture Saint-Laurent or, zoom élégant centré */}
          <motion.h1
            initial={{ opacity: 0, scale: 1.35, filter: 'blur(12px)' }}
            animate={introReady ? { opacity: 1, scale: 1.0, filter: 'blur(0px)' } : { opacity: 0, scale: 1.35, filter: 'blur(12px)' }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontFamily: DISPLAY_FF,
              fontSize: 'clamp(4rem, 13vw, 12rem)',
              fontWeight: 400,
              letterSpacing: '0.04em',
              lineHeight: 0.92,
              transformOrigin: 'center center',
              backgroundImage: `url("/textures/marble-gold.jpg")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 16px 36px rgba(20, 11, 7, 0.35), 0 2px 4px rgba(20, 11, 7, 0.20)',
            }}
            data-testid="hero-brand"
          >
            GRANIPAL
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={introReady ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
            transition={{ delay: 0.55, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-10 mb-6"
            style={{ width: '120px', height: '1px', background: COPPER, opacity: 0.65, transformOrigin: 'center' }}
          />

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={introReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 14 }}
            transition={{ delay: 0.65, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6"
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: 'clamp(0.95rem, 1.35vw, 1.2rem)',
              fontWeight: 500,
              color: COPPER_DARK,
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              textShadow: '0 1px 0 rgba(255,245,225,0.45)',
            }}
            data-testid="hero-tagline"
          >
            Marbrerie d'exception
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            animate={introReady ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
            transition={{ delay: 0.75, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mb-7"
            style={{ width: '60px', height: '1px', background: COPPER, opacity: 0.45, transformOrigin: 'center' }}
          />

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={introReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ delay: 0.85, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="leading-tight"
            style={{
              fontFamily: '"Cormorant Garamond", "Playfair Display", serif',
              fontStyle: 'italic',
              color: INK,
              fontWeight: 400,
              letterSpacing: '0.02em',
              fontSize: 'clamp(1.05rem, 1.4vw, 1.35rem)',
            }}
            data-testid="hero-since"
          >
            Depuis plus de{' '}
            <span style={{ fontSize: 'clamp(1.6rem, 2.6vw, 2.4rem)', fontStyle: 'italic', fontWeight: 500, letterSpacing: '0.005em', color: COPPER_DARK }}>
              25 ans
            </span>
          </motion.p>

          {/* CTAs — heavy glassmorphism with copper sheen on hover */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={introReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ delay: 1.15, duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-5"
          >
            {/* Button 1 — COPPER-tinted glass (harmonious with site, not grey, not yellow) */}
            <Link
              to="/nos-matieres"
              className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden"
              style={{
                color: INK,
                background: 'rgba(184,137,90,0.10)',
                border: '1px solid rgba(139,94,47,0.45)',
                backdropFilter: 'blur(18px)',
                WebkitBackdropFilter: 'blur(18px)',
                boxShadow: 'inset 0 1px 0 rgba(255,250,240,0.25), 0 8px 28px -10px rgba(43,28,18,0.30)',
              }}
              data-testid="discover-materials-btn"
            >
              {/* Shimmer copper sheen on hover */}
              <span
                aria-hidden
                className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none"
                style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,240,210,0.50) 50%, transparent 100%)', transform: 'skewX(-18deg)' }}
              />
              {/* Inner copper accent on hover (no fill change) */}
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ boxShadow: 'inset 0 0 0 1px rgba(139,94,47,0.85), inset 0 0 30px rgba(184,137,90,0.10)' }}
              />
              <span className="relative">Découvrir nos matières</span>
            </Link>

            {/* Button 2 — copper glass with stronger gradient */}
            <Link
              to="/contact"
              className="group relative inline-flex items-center justify-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden"
              style={{
                color: '#FFF8EC',
                background: 'linear-gradient(135deg, rgba(212,168,120,0.85) 0%, rgba(184,137,90,0.92) 45%, rgba(139,94,47,0.95) 100%)',
                border: '1px solid rgba(245,230,208,0.45)',
                backdropFilter: 'blur(10px) saturate(140%)',
                WebkitBackdropFilter: 'blur(10px) saturate(140%)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.28), 0 12px 36px -10px rgba(184,137,90,0.55), 0 0 0 1px rgba(212,168,120,0.20)',
              }}
              data-testid="contact-btn"
            >
              {/* Shimmer line on hover */}
              <span
                aria-hidden
                className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none"
                style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,250,240,0.55) 50%, transparent 100%)', transform: 'skewX(-18deg)' }}
              />
              <span className="relative">Nous contacter</span>
              <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </motion.div>
        </motion.div>

        {/* Localisation — BOTTOM right, no icon, fine lettering */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={introReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ delay: 1.3, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-10 right-6 lg:right-12 hidden md:flex flex-col items-end gap-1.5 select-none z-10"
          data-testid="hero-location"
        >
          <span aria-hidden className="block w-10 h-px mb-2" style={{ background: 'rgba(245,230,208,0.6)' }} />
          <span className="text-[10px] uppercase tracking-[0.32em] text-right" style={{ color: '#F5E6D0', textShadow: '0 1px 4px rgba(0,0,0,0.55)' }}>
            Atelier à Vigneux-sur-Seine&nbsp;(91)
          </span>
          <span className="text-[10px] uppercase tracking-[0.32em] text-right" style={{ color: 'rgba(245,230,208,0.7)', textShadow: '0 1px 4px rgba(0,0,0,0.55)' }}>
            Paris&nbsp;&amp;&nbsp;Île-de-France
          </span>
        </motion.div>

        {/* Scroll indicator — guaranteed center via inset-x-0 + flex justify-center */}
        <div className="absolute inset-x-0 bottom-7 z-10 flex justify-center pointer-events-none">
          <motion.button
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            initial={{ opacity: 0, y: 10 }}
            animate={introReady ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
            transition={{ delay: 1.6, duration: 1 }}
            className="group cursor-pointer flex flex-col items-center gap-3 pointer-events-auto bg-transparent border-0 p-0"
            aria-label="Découvrir la suite"
            data-testid="scroll-cue"
          >
            <span
              className="text-[10.5px] uppercase tracking-[0.42em] font-semibold transition-colors duration-500"
              style={{
                color: COPPER_LIGHT,
                textShadow: '0 1px 6px rgba(0,0,0,0.65), 0 0 18px rgba(184,137,90,0.4)',
              }}
            >
              Découvrez notre savoir-faire
            </span>
            {/* Cascading chevrons */}
            <div className="relative flex flex-col items-center" style={{ height: '22px', width: '24px' }}>
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="absolute left-1/2 -translate-x-1/2"
                  animate={{ y: [-6, 10], opacity: [0, 1, 0] }}
                  transition={{ duration: 1.7, delay: i * 0.28, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: COPPER_LIGHT, strokeWidth: 2 }} />
                </motion.span>
              ))}
            </div>
          </motion.button>
        </div>
      </section>

      {/* No separate bridge — transition is baked into hero bottom fade */}

      {/* ============================================================
          PROCESS — DARK glass cards, copper halos, storytelling
         ============================================================ */}
      <section className="relative py-32 lg:py-44 overflow-hidden" style={{ background: DARK_BG }} data-testid="home-process">
        {/* Halos */}
        <CopperHalo x="20%" y="30%" size={800} opacity={0.18} />
        <CopperHalo x="85%" y="80%" size={700} opacity={0.12} />

        {/* Subtle marble texture */}
        <div aria-hidden className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url(${STONE_DETAIL})`, mixBlendMode: 'screen' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.18] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-16">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.5 }}
            className="text-center mb-24 lg:mb-32"
          >
            <div className="flex items-center justify-center gap-4 mb-7">
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Notre processus</span>
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
            </div>
            <h2
              className="leading-[1.02] mb-6"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.015em',
              }}
            >
              De la <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>matière à la pose</em>
            </h2>
            <p className="text-base font-light max-w-md mx-auto leading-relaxed" style={{ color: 'rgba(245,230,208,0.7)' }}>
              Du choix de la matière jusqu'à la pose chez vous — vous êtes accompagnés par nos marbriers à chaque étape.
            </p>
          </motion.div>

          {/* Steps — horizontal compact timeline (5 columns lg) */}
          <ol className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-5">
            {/* Horizontal connecting line at icon-level (lg only) */}
            <div
              aria-hidden
              className="hidden lg:block absolute top-[42px] left-[8%] right-[8%] h-px"
              style={{ background: `linear-gradient(to right, transparent 0%, ${COPPER}66 14%, ${COPPER}66 86%, transparent 100%)` }}
            />

            {PROCESS.map((s, i) => {
              const { Icon } = s;
              return (
                <motion.li
                  key={s.n}
                  initial={{ opacity: 0, y: 36, scale: 0.92 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.95, delay: i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  viewport={{ once: true, amount: 0.35 }}
                  className="relative flex flex-col items-center text-center group/step"
                  data-testid={`process-step-${i + 1}`}
                >
                  {/* Icon halo + dot on the line */}
                  <motion.div
                    className="relative flex items-center justify-center mb-6"
                    initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
                    whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{ duration: 1.1, delay: i * 0.14 + 0.15, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.3 }}
                  >
                    {/* outer halo (pulsing) */}
                    <motion.div
                      aria-hidden
                      className="absolute -inset-4 rounded-full blur-xl"
                      style={{ background: 'radial-gradient(circle, rgba(184,137,90,0.36), transparent 70%)' }}
                      animate={{ opacity: [0.55, 0.85, 0.55], scale: [1, 1.08, 1] }}
                      transition={{ duration: 3.6, delay: i * 0.4, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    {/* glass circle */}
                    <div
                      className="relative w-[84px] h-[84px] rounded-full flex items-center justify-center backdrop-blur-xl transition-transform duration-500 group-hover/step:scale-105"
                      style={{
                        background: 'linear-gradient(160deg, rgba(245,230,208,0.12) 0%, rgba(184,137,90,0.12) 100%)',
                        border: '1px solid rgba(212,168,120,0.45)',
                        boxShadow: 'inset 0 1px 0 rgba(255,250,240,0.20), 0 10px 30px -8px rgba(0,0,0,0.5)',
                      }}
                    >
                      <Icon className="w-9 h-9" style={{ color: COPPER_LIGHT }} />
                    </div>
                  </motion.div>

                  <motion.span
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: i * 0.14 + 0.4, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.3 }}
                    style={{
                      fontFamily: DISPLAY_FF,
                      fontWeight: 400,
                      fontSize: '0.72rem',
                      color: COPPER_LIGHT,
                      letterSpacing: '0.36em',
                    }}
                  >
                    ÉTAPE&nbsp;{s.n}
                  </motion.span>
                  <motion.h3
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: i * 0.14 + 0.5, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="mt-1.5 mb-2.5"
                    style={{
                      fontFamily: DISPLAY_FF,
                      fontSize: '1.35rem',
                      fontWeight: 400,
                      color: CREAM,
                      letterSpacing: '0.01em',
                      lineHeight: 1.1,
                    }}
                  >
                    {s.title}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: i * 0.14 + 0.6, ease: [0.22, 1, 0.36, 1] }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="text-[13px] font-light leading-relaxed max-w-[230px]"
                    style={{ color: 'rgba(245,230,208,0.62)' }}
                  >
                    {s.text}
                  </motion.p>
                </motion.li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* ============================================================
          REALISATIONS — Category slider with arrows
         ============================================================ */}
      <section className="relative py-28 lg:py-40 overflow-hidden" style={{ background: DARK_BG }} data-testid="home-activities">
        <CopperHalo x="50%" y="15%" size={1100} opacity={0.12} />
        <CopperHalo x="80%" y="80%" size={800} opacity={0.08} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.16] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          {/* Editorial header — prominent title only */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.4 }}
            className="text-center mb-10 lg:mb-14"
          >
            <div className="flex items-center justify-center gap-4 mb-7">
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Portfolio</span>
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
            </div>
            <h2
              className="leading-[0.95]"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(2.8rem, 7vw, 6rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.005em',
              }}
            >
              Nos{' '}
              <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>
                réalisations
              </em>
            </h2>
          </motion.div>

          <RealisationsCategorySlider />

          {/* CTA link to full portfolio */}
          <div className="mt-12 lg:mt-16 text-center">
            <Link
              to="/realisations"
              className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] transition-colors duration-500"
              style={{ color: 'rgba(245,230,208,0.78)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = COPPER_LIGHT; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(245,230,208,0.78)'; }}
              data-testid="see-all-realisations-btn"
            >
              <span className="border-b pb-1" style={{ borderColor: 'rgba(245,230,208,0.2)' }}>Voir toutes les réalisations</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
          SAVOIR-FAIRE — dark elegant background (no atelier image), rich copper sheens
         ============================================================ */}
      <section className="relative overflow-hidden py-28 lg:py-40" style={{ background: DARK_BG_2 }} data-testid="home-savoir-faire">
        {/* Multi copper halos for depth */}
        <CopperHalo x="20%" y="25%" size={1100} opacity={0.18} />
        <CopperHalo x="80%" y="75%" size={900} opacity={0.14} />
        <CopperHalo x="50%" y="50%" size={1400} opacity={0.08} />
        {/* Grain + sheen */}
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 15% 15%, rgba(184,137,90,0.10) 0%, transparent 50%), radial-gradient(ellipse at 85% 85%, rgba(212,168,120,0.07) 0%, transparent 50%)' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.14] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            {/* Left: quote */}
            <motion.div
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              viewport={{ once: true, amount: 0.3 }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-4 mb-7">
                <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
                <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Savoir-faire</span>
              </div>
              <Quote className="w-9 h-9 mb-5 opacity-55" style={{ color: COPPER_LIGHT }} />
              <h2
                className="leading-[1.05] mb-7"
                style={{
                  fontFamily: DISPLAY_FF,
                  fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                  fontWeight: 400,
                  color: CREAM,
                  letterSpacing: '0.02em',
                }}
              >
                Tout est fait <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>à la main</em>,<br />
                par nos marbriers.
              </h2>
              <p className="text-base lg:text-lg leading-relaxed font-light max-w-xl" style={{ color: 'rgba(245,230,208,0.75)' }}>
                Façonnage, polissage et finitions — aucune chaîne, aucune sous-traitance.
                Un atelier, des hommes, et plus de trente ans de pratique du sur-mesure.
              </p>
              <Link
                to="/savoir-faire"
                className="group relative inline-flex items-center gap-3 mt-10 px-7 py-3 uppercase text-[11px] tracking-[0.3em] overflow-hidden transition-all duration-500"
                style={{
                  color: CREAM,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(212,168,120,0.55)',
                  backdropFilter: 'blur(14px) saturate(160%)',
                  WebkitBackdropFilter: 'blur(14px) saturate(160%)',
                }}
                data-testid="savoir-faire-link"
              >
                <span aria-hidden className="absolute inset-y-0 -left-[120%] w-1/2 group-hover:left-[140%] transition-[left] duration-1000 ease-out pointer-events-none" style={{ background: 'linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)', transform: 'skewX(-18deg)' }} />
                <span className="relative">Découvrir l'atelier</span>
                <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
              </Link>
            </motion.div>

            {/* Right: 3 stats stacked */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.16 } } }}
              className="lg:col-span-5 flex flex-col gap-4 lg:gap-5"
            >
              {[
                { value: '25+',  label: "Années d'expertise",  sub: 'Marbrerie depuis 1997' },
                { value: '100%', label: 'Sur-mesure manuel',   sub: 'Aucune chaîne, aucune sous-traitance' },
                { value: '500+', label: 'Projets livrés',      sub: 'Particuliers & professionnels' },
              ].map((s, i) => (
                <motion.div
                  key={s.label}
                  variants={{ hidden: { opacity: 0, x: 20 }, show: { opacity: 1, x: 0, transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] } } }}
                  className="relative px-7 py-7 overflow-hidden flex items-center justify-between gap-6"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(184,137,90,0.05) 60%, rgba(20,11,7,0.20) 100%)',
                    border: '1px solid rgba(212,168,120,0.18)',
                    backdropFilter: 'blur(18px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 24px 50px -28px rgba(0,0,0,0.6)',
                  }}
                >
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: COPPER_LIGHT }}>
                      {s.label}
                    </div>
                    <div className="mt-1 text-[12.5px] font-light" style={{ color: 'rgba(245,230,208,0.55)' }}>
                      {s.sub}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: DISPLAY_FF,
                      fontSize: 'clamp(2.4rem, 4vw, 3.2rem)',
                      color: COPPER_LIGHT,
                      letterSpacing: '0.01em',
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================================================
          MATIÈRES — animated brands
         ============================================================ */}
      <section className="relative py-28 lg:py-36 overflow-hidden" style={{ background: DARK_BG_2 }} data-testid="home-materials">
        <CopperHalo x="70%" y="40%" size={800} opacity={0.12} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.16] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.4 }}
            className="mb-16 max-w-2xl"
          >
            <div className="flex items-center gap-4 mb-7">
              <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Nos matières</span>
            </div>
            <h2
              className="leading-[1.02]"
              style={{
                fontFamily: DISPLAY_FF,
                fontSize: 'clamp(2.25rem, 4.5vw, 3.75rem)',
                fontWeight: 400,
                color: CREAM,
                letterSpacing: '0.01em',
              }}
            >
              Nos partenaires <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>matières</em>
            </h2>
          </motion.div>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-5 mb-14"
          >
            {BRANDS.map((b) => (
              <motion.li
                key={b}
                variants={{
                  hidden: { opacity: 0, y: 15 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                }}
              >
                <Link
                  to={`/nos-matieres?marque=${encodeURIComponent(b)}`}
                  className="block"
                  data-testid={`brand-card-${b.toLowerCase()}`}
                >
                  <GlassCard className="group relative px-6 py-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-500 hover:-translate-y-1 overflow-hidden">
                    <span
                      aria-hidden
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                      style={{ background: `radial-gradient(circle at 50% 50%, rgba(184,137,90,0.22), transparent 70%)` }}
                    />
                    <span
                      className="relative block transition-colors duration-500 group-hover:text-[#F5E6D0]"
                      style={{
                        fontFamily: DISPLAY_FF,
                        fontSize: '1.75rem',
                        fontWeight: 400,
                        color: 'rgba(245,230,208,0.78)',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {b}
                    </span>
                    <span aria-hidden className="relative block h-px mt-3 transition-all duration-500 group-hover:w-12" style={{ background: COPPER_LIGHT, width: '20px' }} />
                  </GlassCard>
                </Link>
              </motion.li>
            ))}
          </motion.ul>

          <Link
            to="/nos-matieres"
            className="group inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] transition-colors duration-500"
            style={{ color: 'rgba(245,230,208,0.7)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = COPPER_LIGHT; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(245,230,208,0.7)'; }}
            data-testid="see-all-materials-btn"
          >
            <span className="border-b pb-1" style={{ borderColor: 'rgba(245,230,208,0.2)' }}>Voir toute la collection</span>
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
          </Link>

          {/* Partner brand marquee — auto-scrolling premium strip */}
          <div className="mt-20 lg:mt-24 pt-12 lg:pt-14 border-t" style={{ borderColor: 'rgba(245,230,208,0.06)' }}>
            <div className="flex items-center gap-4 mb-8 justify-center">
              <span className="block w-8 h-px" style={{ background: 'rgba(212,168,120,0.6)' }} />
              <span className="text-[9.5px] uppercase tracking-[0.40em]" style={{ color: 'rgba(212,168,120,0.78)' }}>
                Nos partenaires
              </span>
              <span className="block w-8 h-px" style={{ background: 'rgba(212,168,120,0.6)' }} />
            </div>
            <BrandMarquee />
          </div>
        </div>
      </section>

      {/* ============================================================
          ESPACE PRO — depth, copper aura
         ============================================================ */}
      <section className="relative py-32 lg:py-40 border-t overflow-hidden" style={{ background: DARK_BG, borderColor: 'rgba(245,230,208,0.06)' }} data-testid="home-pro-area">
        <CopperHalo x="50%" y="50%" size={1100} opacity={0.18} />
        <CopperHalo x="20%" y="20%" size={500} opacity={0.10} />
        <CopperHalo x="80%" y="80%" size={500} opacity={0.10} />

        {/* Decorative stone */}
        <div aria-hidden className="absolute inset-0 opacity-[0.05] bg-cover bg-center" style={{ backgroundImage: `url(${STONE_DETAIL})`, mixBlendMode: 'screen' }} />
        <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.18] mix-blend-overlay" style={{ backgroundImage: GRAIN_BG, backgroundSize: '220px 220px' }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <GlassCard className="px-8 py-14 lg:py-20 lg:px-16">
              <div className="flex items-center justify-center gap-4 mb-8">
                <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
                <span className="text-[10px] uppercase tracking-[0.45em]" style={{ color: COPPER_LIGHT }}>Espace professionnel</span>
                <span className="block w-10 h-px" style={{ background: COPPER_LIGHT }} />
              </div>
              <h2
                className="leading-[1.05] mb-8"
                style={{
                  fontFamily: DISPLAY_FF,
                  fontSize: 'clamp(1.85rem, 3.6vw, 3rem)',
                  fontWeight: 400,
                  color: CREAM,
                  letterSpacing: '0.01em',
                }}
              >
                Vous êtes un <em style={{ color: COPPER_LIGHT, fontStyle: 'italic', fontWeight: 400, fontFamily: '"Cormorant Garamond", serif' }}>professionnel</em> ?
              </h2>
              <p className="max-w-lg mx-auto mb-10 font-light leading-relaxed" style={{ color: 'rgba(245,230,208,0.6)' }}>
                Accédez à votre configurateur de devis en ligne — tarifs professionnels et PDF en un clic.
              </p>
              <Link
                to="/login"
                className="group relative inline-flex items-center gap-3 px-10 py-4 uppercase text-[11px] tracking-[0.3em] transition-all duration-500 overflow-hidden"
                style={{
                  background: COPPER_GRAD,
                  color: '#FFF8EC',
                  boxShadow: '0 12px 38px rgba(184,137,90,0.42), inset 0 1px 0 rgba(255,255,255,0.18)',
                }}
                data-testid="pro-access-cta-btn"
              >
                <span aria-hidden className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 50%, rgba(0,0,0,0.18) 100%)' }} />
                <span className="relative">Accéder à l'Espace Pro</span>
                <ArrowRight className="relative w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-500" />
              </Link>
            </GlassCard>
          </motion.div>
        </div>
      </section>
    </VitrineLayout>
  );
}
